<template>
  <div class="home-wrapper h-screen ml-56">
    <!-- Page content -->
    <div class="w-full h-full overflow-y-scroll bg-truegray-100">
      <!-- Page header -->
      <PageHeader :overline="headerConfig.overline" :title="headerConfig.title">
        <template #title>
          <div class="flex items-center space-x-4 w-3/4">
            <div class="px-8 lg:border-l-8 border-truegray-100">
              <h2 class="text-cw-900 font-montserrat mb-10">
                {{ headerConfig.title }}
              </h2>
              <PostsFilter @submit-filters="submitFilters" />
            </div>
          </div>
        </template>
      </PageHeader>
      <!-- /Page header -->

      <!-- Posts content -->
      <div class="w-full py-16 px-8 md:px-16 lg:px-26 xl:px-32 2xl:px-40">
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="h-52 flex flex-col justify-center items-center"
        >
          <lottie :options="loadingAnimationOptions" />
          <span class="text-center">{{ loadingText }}</span>
        </div>
        <!-- /Loading -->

        <!-- Posts cards -->
        <template v-else>
          <div class="w-full">
            <button
              v-if="canPost"
              class="btn btn-cw-cd-primary flex items-center cw-gradient"
              :disabled="!canPost"
              @click="openNewPostModal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              New Post
            </button>
            <div v-else class="mb-4">
              <p class="text-red-400 mb-4">
                You have used all of your purchased posts. Purchase more to keep
                posting!
              </p>

              <button
                class="btn btn-trans-black btn-cw-cd p-3 mt-6 mb-8 font-normal"
                @click="showPostPackModal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="w-4 h-4 mr-1"
                >
                  <path
                    d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                  />
                </svg>
                Posts
              </button>
            </div>
          </div>
          <div
            v-if="posts.length"
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6"
          >
            <PostsCard
              v-for="(post, i) in posts"
              :id="post.id"
              :key="i"
              :ref="'posts-' + i"
              :post="post"
              :admin_menu="true"
              :cloud="true"
              :contributor="true"
            />
          </div>
          <p v-else class="text-md">We could not find any posts...</p>
          <!-- Pagination -->
          <div
            v-if="pagination.previouspage || pagination.nextpage"
            class="flex justify-between items-center h-10 mt-8"
          >
            <span
              :disabled="!pagination.previouspage"
              :class="[
                !pagination.previouspage
                  ? 'opacity-25 cursor-not-allowed'
                  : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
              ]"
              class="text-gray-500"
              @click="updatePosts(pagination.previouspage)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 17l-5-5m0 0l5-5m-5 5h12"
                />
              </svg>
            </span>
            <span class="text-center text-xs"
              >Page {{ pagination.current_page }} of
              {{ pagination.total_pages }}</span
            >
            <span
              :disabled="!pagination.nextpage"
              :class="[
                !pagination.nextpage
                  ? 'opacity-25 cursor-not-allowed'
                  : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
              ]"
              class="text-gray-500"
              @click="updatePosts(pagination.nextpage)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
          </div>
        </template>
        <!-- /Posts cards -->
      </div>
      <!-- /Posts content -->
    </div>
    <!-- /Page content -->
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { defineComponent, computed, ref, onMounted, set, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useStore } from '../../../js/store/index.js'
import PostsFilter from '../components/PostsFilter.vue'
import PostsCard from '../../../shared/components/PostsCard.vue'
import PageHeader from '../components/PageHeader.vue'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'

export default defineComponent({
  name: 'Posts',

  components: {
    PageHeader,
    PostsFilter,
    PostsCard,
  },

  setup() {
    const store = useStore()
    const route = useRoute()

    const drafts = computed(() => route.path === '/cloud/drafts')

    const headerConfig = computed(() => {
      return {
        overline: 'posts',
        title: drafts.value ? 'My Cloud Drafts' : 'My Cloud Posts',
      }
    })

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)
    const loadingText = computed(() =>
      drafts.value ? 'Loading drafts' : 'Loading posts',
    )
    const filters = ref(null)
    const pagination = ref({
      current_page: null,
      total_pages: null,
      per_page: 9,
      next_page: null,
      previous_page: null,
      last_page_url: null,
      next_page_url: null,
      prev_page_url: null,
      path: null,
      nextpage: null,
      previouspage: null,
    })

    const organisation = computed(() => {
      return store.getters['contributors/getOrganisation']
    })

    const organisationId = computed(
      () => store.getters['contributors/getOrganisationId'],
    )

    const posts = computed(() => {
      return store.getters['contributors/getPosts']
    })

    const canPost = computed(() => {
      return store.getters['contributors/canPost']
    })

    const openNewPostModal = () => {
      // @todo pass modal data to use portal endpoints and cloud styles
      EventBus.$emit('showModal', 'new-post')
    }

    const showPostPackModal = () => {
      EventBus.$emit('showModal', {
        action: 'cw-contributor-post-pack',
      })
    }

    const submitFilters = async (payload, callback) => {
      filters.value = payload
      await updatePosts()
      callback()
    }

    const updatePagination = (pag) => {
      // Pagination total = ('total # of posts' divided by 'per_page') + 1
      // Pagination doesn't show when not needed so +1 is never wrong on single page
      const total_pages = Math.floor(pag.total / pag.per_page) + 1
      set(pagination.value, 'current_page', pag.current_page)
      set(pagination.value, 'total_pages', total_pages)
      set(pagination.value, 'per_page', pag.per_page)
      set(pagination.value, 'last_page_url', pag.last_page_url)
      set(pagination.value, 'next_page_url', pag.next_page_url)
      set(pagination.value, 'prev_page_url', pag.prev_page_url)
      set(pagination.value, 'next_page_path', pag.next_page_path)

      let prev = null
      if (pagination.value.prev_page_url) {
        prev = pagination.value.prev_page_url.split('=')
        prev = prev[1]
      }

      let next = null
      if (pagination.value.next_page_url) {
        next = pagination.value.next_page_url.split('=')
        next = next[1]
      }

      set(pagination.value, 'previouspage', prev)
      set(pagination.value, 'nextpage', next)
    }

    const updatePosts = async (page = null) => {
      if (drafts.value) {
        filters.value = {
          ...filters.value,
          draft: !!drafts.value,
        }
      }
      const payload = {
        // add page and filters to payload, if they are defined
        ...(!!filters.value && { search: filters.value }),
        ...(!!page && { page }),
      }

      const pagination = await store.dispatch(
        'contributors/dispatchUpdatePosts',
        payload,
      )

      updatePagination(pagination)
    }

    const clearFilters = async () => {
      filters.value = null
      EventBus.$emit('clearPostFilters')
    }

    watch([() => route.path, organisationId], () => init())

    const init = async () => {
      isLoading.value = true
      clearFilters()
      if (drafts.value) {
        filters.value = {
          ...filters.value,
          draft: drafts.value,
        }
      }
      const payload = {
        // add page and filters to payload, if they are defined
        ...(!!filters.value && { search: filters.value }),
      }
      const pagination = await store.dispatch(
        'contributors/dispatchInitPagePosts',
        payload,
      )
      updatePagination(pagination)
      isLoading.value = false
    }

    onMounted(() => {
      init()

      EventBus.$on('getPostsWithFilterOptions', () => updatePosts())
    })

    return {
      loadingAnimationOptions,
      isLoading,
      loadingText,

      headerConfig,
      organisation,
      pagination,
      posts,
      drafts,
      openNewPostModal,
      submitFilters,
      updatePosts,
      canPost,
      showPostPackModal,
    }
  },
})
</script>
